import moment from "moment";
import { CsvCell } from "ag-grid-community";
import { organizationStore } from "../../../store/organization";
import { eventStore } from "../../../store/event";
import { reportFiltersStore } from "../../../store/reportFilters";

const { getState: getOrganizationState } = organizationStore;
const { getState: getEventState } = eventStore;

interface BuildReportCsvHeaderOptions {
  /** The display name of the report (e.g. "Transactions Report") */
  reportName: string;
  /** The URL search string from useLocation().search */
  searchString: string;
  /** Optional ID→name map for resolving vendor filter IDs to display names */
  vendorNameMap?: Record<string, string>;
  /** Optional ID→name map for resolving location filter IDs to display names */
  locationNameMap?: Record<string, string>;
}

/**
 * Builds a CSV prepend header containing the report name, export timestamp,
 * and a summary of all active filters (with human-readable names where possible).
 *
 * Usage:
 * ```ts
 * gridRef.current.exportDataAsCsv({
 *   ...params,
 *   prependContent: buildReportCsvHeader({
 *     reportName: "Transactions Report",
 *     searchString: location.search,
 *   }),
 * });
 * ```
 */
export const buildReportCsvHeader = ({
  reportName,
  searchString,
  vendorNameMap,
  locationNameMap,
}: BuildReportCsvHeaderOptions): CsvCell[][] => {
  const rows: CsvCell[][] = [];
  const cell = (value: string): CsvCell => ({ data: { value } });

  // Row 1: Report name
  rows.push([cell(reportName)]);

  // Row 2: Export timestamp
  rows.push([
    cell(`Exported: ${moment().format("ddd, MMM DD, YYYY h:mm a")}`),
  ]);

  // Lookup helpers from stores
  const orgState = getOrganizationState();
  const eventState = getEventState();
  const allEvents: any[] = eventState.allEvents || [];
  const allOrgs: any[] =
    orgState.allOrganizations || orgState.organizations || [];

  const lookupEventName = (id: any): string => {
    const evt = allEvents.find((e: any) => String(e.id) === String(id));
    return evt ? `${evt.name} (${id})` : String(id);
  };

  const lookupOrgName = (id: any): string => {
    const org = allOrgs.find((o: any) => String(o.id) === String(id));
    return org ? `${org.name} (${id})` : String(id);
  };

  const lookupSubEventName = (id: any): string => {
    for (const evt of allEvents) {
      const subEvt = evt.sub_events?.find(
        (se: any) => String(se.id) === String(id)
      );
      if (subEvt) return subEvt.name;
    }
    return String(id);
  };

  const reportFiltersState = reportFiltersStore.getState();
  const allVendors: any[] = reportFiltersState.allVendors || [];
  const allLocations: any[] = reportFiltersState.allLocations || [];

  const resolveVendorNames = (ids: any[]): string => {
    return ids
      .map((id) => {
        const name =
          vendorNameMap?.[String(id)] ??
          allVendors.find((v: any) => String(v.id) === String(id))?.name;
        return name ? `${name} (${id})` : String(id);
      })
      .join(", ");
  };

  const resolveLocationNames = (ids: any[]): string => {
    return ids
      .map((id) => {
        const name =
          locationNameMap?.[String(id)] ??
          allLocations.find((l: any) => String(l.id) === String(id))?.name;
        return name ? `${name} (${id})` : String(id);
      })
      .join(", ");
  };

  // Parse active filters from URL
  const search = new URLSearchParams(searchString);
  const where = search.get("where");
  const filters: string[] = [];

  if (where) {
    try {
      const whereObj = JSON.parse(where);

      // Date range — check common date field patterns
      // Reports may use transaction_hour or transaction_date in _and[0] / _and[1]
      const dateFields = [
        "transaction_hour",
        "transaction_date",
        "created_at",
      ];
      for (const field of dateFields) {
        const dateFrom =
          whereObj?._and?.[0]?.[field]?._gte ??
          whereObj?._and?.[0]?.[field]?._gt;
        const dateTo =
          whereObj?._and?.[1]?.[field]?._lte ??
          whereObj?._and?.[1]?.[field]?._lt;
        if (dateFrom) {
          filters.push(
            `Date From: ${moment(dateFrom).format("MMM DD, YYYY h:mm a")}`
          );
        }
        if (dateTo) {
          filters.push(
            `Date To: ${moment(dateTo).format("MMM DD, YYYY h:mm a")}`
          );
        }
        if (dateFrom || dateTo) break; // found the date field, stop checking
      }

      // Event
      if (whereObj?.event_id?._eq) {
        filters.push(`Event: ${lookupEventName(whereObj.event_id._eq)}`);
      } else if (whereObj?.event_id?._in) {
        const names = whereObj.event_id._in.map((id: any) =>
          lookupEventName(id)
        );
        filters.push(`Events: ${names.join(", ")}`);
      }

      // Vendor
      if (whereObj?.vendor_id?._eq) {
        filters.push(
          `Vendor: ${resolveVendorNames([whereObj.vendor_id._eq])}`
        );
      } else if (whereObj?.vendor_id?._in) {
        filters.push(
          `Vendors: ${resolveVendorNames(whereObj.vendor_id._in)}`
        );
      }

      // Location
      if (whereObj?.location_id?._eq) {
        filters.push(
          `Location: ${resolveLocationNames([whereObj.location_id._eq])}`
        );
      } else if (whereObj?.location_id?._in) {
        filters.push(
          `Locations: ${resolveLocationNames(whereObj.location_id._in)}`
        );
      }

      // Organization (from URL)
      if (whereObj?.organization_id?._eq) {
        filters.push(
          `Organization: ${lookupOrgName(whereObj.organization_id._eq)}`
        );
      } else if (whereObj?.organization_id?._in) {
        const names = whereObj.organization_id._in.map((id: any) =>
          lookupOrgName(id)
        );
        filters.push(`Organizations: ${names.join(", ")}`);
      }

      // Username / Clerk — ReportFilters may use "username" or "clerk_id" key
      if (whereObj?.username?._eq) {
        filters.push(`Username: ${whereObj.username._eq}`);
      } else if (whereObj?.username?._in) {
        filters.push(`Usernames: ${whereObj.username._in.join(", ")}`);
      } else if (whereObj?.clerk_id?._eq) {
        filters.push(`Username: ${whereObj.clerk_id._eq}`);
      } else if (whereObj?.clerk_id?._in) {
        filters.push(`Usernames: ${whereObj.clerk_id._in.join(", ")}`);
      }

      // Sub-event (ID is stored as a separate URL param, not in the where clause)
      if (whereObj?.sub_event_id?._eq) {
        filters.push(
          `Sub-Event: ${lookupSubEventName(whereObj.sub_event_id._eq)}`
        );
      } else if (whereObj?.sub_event_id?._in) {
        const names = whereObj.sub_event_id._in.map((id: any) =>
          lookupSubEventName(id)
        );
        filters.push(`Sub-Events: ${names.join(", ")}`);
      }
    } catch {
      // If where param can't be parsed, skip filters
    }
  }

  // Sub-event from URL param (ReportFilters stores it separately, not in the where clause)
  const subEventParam = search.get("subEvent");
  if (subEventParam && !filters.some((f) => f.startsWith("Sub-Event"))) {
    filters.push(`Sub-Event: ${lookupSubEventName(subEventParam)}`);
  }

  // Add organization name from store if not already captured from URL
  if (
    orgState.organizationId &&
    !filters.some((f) => f.startsWith("Organization:"))
  ) {
    filters.push(`Organization: ${lookupOrgName(orgState.organizationId)}`);
  }

  // Add event name from store (global event filter) if not already captured
  if (eventState.eventId && !filters.some((f) => f.startsWith("Event:"))) {
    filters.push(`Event: ${lookupEventName(eventState.eventId)}`);
  }

  if (filters.length > 0) {
    rows.push([cell(`Filters: ${filters.join(" | ")}`)]);
  } else {
    rows.push([cell("Filters: None")]);
  }

  // Blank separator row
  rows.push([cell("")]);

  return rows;
};
